import React, { useState } from 'react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';
import { X, Combine, ArrowLeft } from 'lucide-react';


/**
 * Merge dialog — merge ``source`` rule INTO ``target`` rule. Telemetry
 * (match/reject/override counts) is summed on the target; source is
 * archived (not deleted) so the audit trail remains intact.
 */
export default function MergeDialog({ target, source, onClose, onMerged }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy]     = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(
        '/bank-rules/merge',
        { target_id: target.id, source_id: source.id, reason: reason || undefined },
      );
      toast.success(`تَم دَمج «${source.name}» في «${target.name}»`);
      onMerged?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل الدَّمج');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="merge-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl" dir="rtl">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <Combine className="w-5 h-5 text-sky-600" />
            <div className="text-base font-bold">دَمج قاعِدَتَين</div>
          </div>
          <button onClick={onClose} data-testid="merge-close"
                  className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            ⚠ سيَتِم جَمع تِلِيمتري المَصدَر (إطلاقات / رَفض / تَجاوُز) داخِل الهَدَف، وأَرشَفَة المَصدَر.
            هَذِه عَمَلِيَّة قابِلَة لِلتَّراجُع عَبر فَك أَرشَفَة المَصدَر يَدَوِياً، لَكِنّ الأَرقام تَبقى مُجَمَّعَة.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-3 items-center">
            <div className="rounded-xl border bg-sky-50/50 border-sky-200 p-3" data-testid="merge-target">
              <div className="text-[10px] uppercase font-semibold text-sky-700">الهَدَف</div>
              <div className="text-sm font-bold text-sky-900 mt-0.5">{target.name}</div>
              <div className="text-[11px] font-mono text-sky-700 mt-1">
                {target.pattern_type}: {target.pattern}
              </div>
              <div className="mt-2 text-[10px] text-sky-800 font-mono">
                إطلاقات: {target.match_count || 0} · رَفض: {target.reject_count || 0}
              </div>
            </div>
            <ArrowLeft className="w-6 h-6 text-slate-400 mx-auto" />
            <div className="rounded-xl border bg-zinc-50 border-zinc-200 p-3" data-testid="merge-source">
              <div className="text-[10px] uppercase font-semibold text-zinc-700">المَصدَر (سيُؤَرشَف)</div>
              <div className="text-sm font-bold text-zinc-900 mt-0.5">{source.name}</div>
              <div className="text-[11px] font-mono text-zinc-700 mt-1">
                {source.pattern_type}: {source.pattern}
              </div>
              <div className="mt-2 text-[10px] text-zinc-800 font-mono">
                إطلاقات: {source.match_count || 0} · رَفض: {source.reject_count || 0}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-700 font-semibold">سَبَب الدَّمج:</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="مثال: نَمطان يُحَقِّقان نَفس الهَدَف"
                   data-testid="merge-reason"
                   className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm mt-1" />
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose}
                  data-testid="merge-cancel"
                  className="text-sm font-semibold rounded-lg bg-white border border-slate-300 hover:bg-slate-100 px-4 py-2">
            إلغاء
          </button>
          <button onClick={apply} disabled={busy}
                  data-testid="merge-apply"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 disabled:opacity-50">
            <Combine className="w-4 h-4" /> {busy ? '…' : 'دَمج'}
          </button>
        </div>
      </div>
    </div>
  );
}
